/**
 * Browser Geolocation helpers for capturing a party's physical location.
 */

export interface Coordinates {
    lat: number;
    lng: number;
    accuracy: number; // meters
}

/**
 * Wraps navigator.geolocation.getCurrentPosition in a Promise and maps the
 * various failure modes to friendly, user-facing messages.
 *
 * Geolocation requires a secure context (HTTPS or localhost) — satisfied in
 * dev (localhost) and prod (Cloudflare HTTPS).
 */
export const getCurrentCoordinates = (): Promise<Coordinates> => {
    return new Promise((resolve, reject) => {
        if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
            reject(new Error("Location isn't available on this device/browser."));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                });
            },
            (error) => {
                let message = "Couldn't get your location. Try again.";
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        message =
                            'Location permission denied. Enable it in your browser/PWA settings.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        message = "Couldn't get a location fix. Try again outdoors.";
                        break;
                    case error.TIMEOUT:
                        message = 'Location request timed out. Try again.';
                        break;
                }
                reject(new Error(message));
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0,
            }
        );
    });
};

/**
 * Formats coordinates as a clean "lat,lng" decimal-degrees string for storage in
 * geo_location. This is the presentation-agnostic format the rest of the system
 * already uses (web app forms expect "latitude, longitude"), so any client can
 * build its own link or export the raw coordinates for analytics/other maps.
 * 6 decimal places ≈ 0.11 m precision.
 */
export const formatCoordinates = (lat: number, lng: number): string =>
    `${lat.toFixed(6)},${lng.toFixed(6)}`;

/**
 * Builds a Google Maps URL for raw coordinates (used to preview/verify a freshly
 * captured fix before saving).
 */
export const buildMapsUrl = (lat: number, lng: number): string =>
    `https://www.google.com/maps?q=${lat},${lng}`;

/**
 * Turns a stored geo_location value into an openable Google Maps URL. Handles all
 * the shapes that exist in the data:
 *  - legacy full URLs (http/https) -> opened as-is
 *  - "lat,lng" coordinates or free-text addresses -> Google Maps "?q=" query
 */
export const geoLocationToMapsUrl = (value: string): string => {
    const trimmed = value.trim();
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    return `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}`;
};
